import {
  convertMinorUnits,
  getMinorUnitsSync,
  startOfAccountingMonth,
  endOfAccountingMonth,
  normalizeMonthStartDay,
} from '@sharecost/shared';
import type { Group } from '@sharecost/shared';

export type BudgetPeriod = 'monthly' | 'total';

export interface BudgetStatus {
  budgetAmountMinor: number;
  spentAmountMinor: number;
  remainingAmountMinor: number;
  percentage: number;
  currency: string;
  alertThreshold: number;
  period: BudgetPeriod;
  /** Day (1-28) the monthly period starts; 1 = calendar month */
  budgetStartDay: number;
  isOverBudget: boolean;
  isApproachingLimit: boolean;
  isApproximate: boolean;
  periodStart: string;
  periodEnd: string;
}

export interface BudgetExpenseRow {
  amount_minor_src: number | string;
  amount_minor_usd: number | string;
  currency_code_src: string;
  expense_date: string;
}

/**
 * Monthly budgets run from the group's budget start day (e.g. payday) to the
 * day before the next one; mirrors mobile BudgetService.getPeriodBoundaries.
 */
export function getPeriodBoundaries(
  period: BudgetPeriod,
  groupCreatedAt: string,
  budgetStartDay: number | null | undefined = 1
): { start: Date; end: Date } {
  if (period === 'monthly') {
    const now = new Date();
    const startDay = normalizeMonthStartDay(budgetStartDay);
    return { start: startOfAccountingMonth(now, startDay), end: endOfAccountingMonth(now, startDay) };
  }
  return { start: new Date(groupCreatedAt), end: new Date() };
}

export function isGroupBudgetEnabled(
  group: Pick<Group, 'budget_enabled' | 'budget_amount_minor' | 'budget_period'>
): boolean {
  return (
    group.budget_enabled === true &&
    typeof group.budget_amount_minor === 'number' &&
    group.budget_amount_minor > 0 &&
    (group.budget_period === 'monthly' || group.budget_period === 'total')
  );
}

/**
 * Compute spent amount in the group currency for the given expense rows,
 * filtering by period. Mirrors mobile BudgetService logic:
 * - same-currency expenses → sum `amount_minor_src` (native precision).
 * - cross-currency expenses → convert `amount_minor_usd` via USD rate map.
 */
export function computeBudgetStatus(
  group: Pick<
    Group,
    | 'currency_code'
    | 'budget_amount_minor'
    | 'budget_period'
    | 'budget_alert_threshold'
    | 'budget_start_day'
    | 'created_at'
  >,
  expenses: BudgetExpenseRow[],
  rates: Map<string, number>
): BudgetStatus | null {
  const budgetAmountMinor = group.budget_amount_minor ?? 0;
  const period = (group.budget_period ?? 'monthly') as BudgetPeriod;
  const alertThreshold = group.budget_alert_threshold ?? 80;
  const currency = group.currency_code;
  const groupMinorUnits = getMinorUnitsSync(currency);

  const budgetStartDay = normalizeMonthStartDay(group.budget_start_day);
  const { start, end } = getPeriodBoundaries(period, group.created_at, budgetStartDay);
  const startMs = start.getTime();
  const endMs = end.getTime();

  const groupRate = rates.get(currency) ?? 1;
  const usdToGroup = groupRate > 0 ? 1 / groupRate : 1;

  let spentMinor = 0;
  let isApproximate = false;

  for (const expense of expenses) {
    const expenseMs = new Date(expense.expense_date).getTime();
    if (expenseMs < startMs || expenseMs > endMs) continue;

    if (expense.currency_code_src === currency) {
      spentMinor += Number(expense.amount_minor_src);
    } else {
      spentMinor += convertMinorUnits(
        Number(expense.amount_minor_usd),
        usdToGroup,
        2,
        groupMinorUnits
      );
      isApproximate = true;
    }
  }

  const remainingAmountMinor = budgetAmountMinor - spentMinor;
  const percentage =
    budgetAmountMinor > 0 ? (spentMinor / budgetAmountMinor) * 100 : 0;
  const isOverBudget = spentMinor > budgetAmountMinor;
  const isApproachingLimit = percentage >= alertThreshold && !isOverBudget;

  return {
    budgetAmountMinor,
    spentAmountMinor: spentMinor,
    remainingAmountMinor,
    percentage,
    currency,
    alertThreshold,
    period,
    budgetStartDay,
    isOverBudget,
    isApproachingLimit,
    isApproximate,
    periodStart: start.toISOString(),
    periodEnd: end.toISOString(),
  };
}
